import { useEffect, useRef, useState } from "react";
import type { QuizDetail, ScoringMode } from "../../../shared/types";
import { questionEyebrow, OPTION_KEYS, mediaUrl } from "../../../shared/format";
import { OptGlyph } from "../../components/motifs";
import { Ico } from "../../components/shell";

function EyeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
import { OvenTimer } from "../../components/primitives";
import { StateError, StateLoading } from "../states/States";
import { api, ApiError, type QuizSaveBody } from "../../api";
import LaunchModal from "../host/LaunchModal";
import { alertDialog } from "../../components/Dialog";
import { navigate } from "../../router";
import {
  QuestionForm,
  blankQuestion,
  questionIssue,
  draftToInput,
  type DraftQuestion,
} from "../../components/QuestionForm";
import { EmojiPicker } from "../../components/EmojiPicker";
import BankPickerModal from "../../components/BankPickerModal";

interface Draft {
  title: string;
  description: string;
  scoring: ScoringMode;
  status: "draft" | "published";
  coverEmoji?: string;
  questions: DraftQuestion[];
}

function fromDetail(q: QuizDetail): Draft {
  return {
    title: q.title,
    description: q.description ?? "",
    scoring: q.scoring,
    status: q.status,
    coverEmoji: q.coverEmoji,
    questions: q.questions.map((qq) => ({
      type: qq.type,
      prompt: qq.prompt,
      category: qq.category ?? "",
      chefNote: qq.chefNote ?? "",
      timeLimit: qq.timeLimit,
      points: qq.points,
      imageKey: qq.imageKey,
      options: qq.options.map((o) => ({ text: o.text, correct: o.correct, imageKey: o.imageKey })),
    })),
  };
}

function toPayload(d: Draft, status?: "draft" | "published"): QuizSaveBody {
  return {
    title: d.title.trim() || "Untitled bake",
    description: d.description.trim() || undefined,
    scoring: d.scoring,
    status: status ?? d.status,
    coverEmoji: d.coverEmoji,
    questions: d.questions.map((q) => ({
      type: q.type,
      prompt: q.prompt.trim(),
      category: q.category.trim() || undefined,
      chefNote: q.chefNote.trim() || undefined,
      timeLimit: q.timeLimit,
      points: q.points,
      imageKey: q.imageKey,
      options: q.options.map((o) => ({ text: o.text.trim(), correct: o.correct, imageKey: o.imageKey })),
    })),
  };
}

export default function QuizBuilder({ quizId }: { quizId?: string }) {
  const [resolvedId, setResolvedId] = useState<string | null>(quizId ?? null);
  const creatingRef = useRef(false);

  // No id → create a fresh draft and swap the URL to /builder/:id.
  useEffect(() => {
    if (quizId) {
      setResolvedId(quizId);
      return;
    }
    if (creatingRef.current) return;
    creatingRef.current = true;
    api
      .createQuiz("Untitled bake", "speed")
      .then((r) => {
        navigate(`/builder/${r.quiz.id}`, true);
        setResolvedId(r.quiz.id);
      })
      .catch(() => setResolvedId("__error__"));
  }, [quizId]);

  if (!resolvedId) return <StateLoading />;
  if (resolvedId === "__error__") return <StateError />;
  return <Editor key={resolvedId} quizId={resolvedId} />;
}

function Editor({ quizId }: { quizId: string }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sel, setSel] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "dirty">("idle");
  const [hostBusy, setHostBusy] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState<boolean>(() => {
    try {
      return localStorage.getItem("bk.builder.preview") !== "0";
    } catch {
      return true;
    }
  });
  const dirtyRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    try {
      localStorage.setItem("bk.builder.preview", showPreview ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [showPreview]);

  useEffect(() => {
    api
      .getQuiz(quizId)
      .then((r) => setDraft(fromDetail(r.quiz)))
      .catch(() => setLoadError(true));
  }, [quizId]);

  // Debounced autosave whenever the draft changes after load.
  function markDirty(next: Draft) {
    setDraft(next);
    dirtyRef.current = true;
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void save(next), 900);
  }

  async function save(d: Draft, status?: "draft" | "published"): Promise<boolean> {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    try {
      const r = await api.saveQuiz(quizId, toPayload(d, status));
      dirtyRef.current = false;
      setSaveState("saved");
      // keep status in sync if we just published
      if (status) setDraft((cur) => (cur ? { ...cur, status } : cur));
      return !!r.quiz;
    } catch {
      setSaveState("dirty");
      return false;
    }
  }

  if (loadError) return <StateError />;
  if (!draft) return <StateLoading />;

  const selIdx = Math.min(sel, Math.max(0, draft.questions.length - 1));
  const q = draft.questions.length > 0 ? draft.questions[selIdx] : null;
  const issues = draft.questions.map(questionIssue);
  const valid = draft.questions.length > 0 && issues.every((i) => !i);

  function patchQuestion(mut: (q: DraftQuestion) => DraftQuestion) {
    markDirty({
      ...draft!,
      questions: draft!.questions.map((qq, i) => (i === selIdx ? mut(qq) : qq)),
    });
  }

  function addQuestion() {
    const next = { ...draft!, questions: [...draft!.questions, blankQuestion()] };
    setSel(next.questions.length - 1);
    markDirty(next);
  }

  function addFromBank(qs: DraftQuestion[]) {
    if (qs.length === 0) return;
    const next = { ...draft!, questions: [...draft!.questions, ...qs] };
    setSel(next.questions.length - 1);
    markDirty(next);
  }

  function deleteQuestion(i: number) {
    if (draft!.questions.length <= 1) return;
    const questions = draft!.questions.filter((_, j) => j !== i);
    setSel((s) => Math.max(0, Math.min(s, questions.length - 1)));
    markDirty({ ...draft!, questions });
  }

  function moveQuestion(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= draft!.questions.length) return;
    const questions = [...draft!.questions];
    [questions[i], questions[j]] = [questions[j], questions[i]];
    setSel(j);
    markDirty({ ...draft!, questions });
  }

  function publishAndHost() {
    if (!valid) {
      setSaveState("dirty");
      return;
    }
    setLaunchOpen(true);
  }

  /** The popup calls this before creating the session — publish the latest draft. */
  async function saveBeforeLaunch(): Promise<boolean> {
    return save(draft!, "published");
  }

  const saveLabel =
    saveState === "saving" ? "Saving…" : saveState === "saved" ? "All saved" : saveState === "dirty" ? "Unsaved changes" : "Ready";
  const savedTone = saveState === "saved";

  return (
    <div className="atl" style={{ display: "flex", flexDirection: "column" }}>
      {/* Focus-mode top bar — back link replaces the global sidebar */}
      <header
        style={{
          height: 78,
          flex: "0 0 78px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "0 28px",
          borderBottom: "1px solid var(--atl-line)",
          background: "var(--atl-surface)",
        }}
      >
        <button className="atl-btn atl-btn--link" style={{ padding: "8px 2px", fontSize: 13.5 }} onClick={() => navigate("/quizzes")}>
          ← Quizzes
        </button>
        <span style={{ width: 1, height: 34, background: "var(--atl-line)" }} />
        <div>
          <h1 className="atl-serif" style={{ fontSize: 25 }}>Quiz Builder</h1>
          <div className="atl-eyebrow" style={{ marginTop: 4 }}>
            {`${draft.questions.length} QUESTION${draft.questions.length === 1 ? "" : "S"} · ${draft.status === "published" ? "PUBLISHED" : "DRAFT"}`}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <span
          className="atl-chip"
          style={{
            borderColor: savedTone ? "#cfe0c2" : "var(--atl-line)",
            color: savedTone ? "#5d7344" : "var(--atl-soft)",
            background: savedTone ? "#f1f5ea" : "var(--atl-surface)",
          }}
        >
          <span className="atl-dot" style={{ background: savedTone ? "var(--atl-c)" : "var(--atl-gold)" }} /> {saveLabel}
        </span>
        <button
          className="atl-btn atl-btn--ghost"
          aria-pressed={showPreview}
          title={showPreview ? "Hide live preview" : "Show live preview"}
          onClick={() => setShowPreview((v) => !v)}
          style={{
            borderColor: showPreview ? "#e2c5ca" : "var(--atl-line)",
            color: showPreview ? "var(--atl-accent)" : "var(--atl-soft)",
            background: showPreview ? "#fbf2f3" : "transparent",
          }}
        >
          <EyeIcon size={15} /> Preview
        </button>
        <button className="atl-btn atl-btn--ghost" onClick={() => save(draft)}>
          <Ico name="check" size={15} /> Save
        </button>
        <button className="atl-btn atl-btn--primary" disabled={!valid || hostBusy} onClick={publishAndHost}>
          <Ico name="play" size={16} /> {hostBusy ? "Opening…" : "Save & Host"}
        </button>
      </header>

      {/* Quiz-level settings strip */}
      <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "13px 30px",
            borderBottom: "1px solid var(--atl-line)",
            background: "var(--atl-surface)",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }} title="Quiz emoji — shown on the lobby and your dashboard">
            <EmojiPicker value={draft.coverEmoji} onChange={(emoji) => markDirty({ ...draft, coverEmoji: emoji })} />
          </div>
          <input
            className="atl-serif"
            value={draft.title}
            onChange={(e) => markDirty({ ...draft, title: e.target.value })}
            placeholder="Quiz title"
            style={{ fontSize: 19, fontWeight: 600, border: "none", background: "transparent", color: "var(--atl-ink)", outline: "none", maxWidth: 280 }}
          />
          <input
            value={draft.description}
            onChange={(e) => markDirty({ ...draft, description: e.target.value })}
            placeholder="Short description (optional)"
            style={{ flex: 1, minWidth: 160, border: "none", background: "transparent", fontSize: 13.5, fontWeight: 500, color: "var(--atl-soft)", outline: "none", fontFamily: "var(--atl-sans)" }}
          />
          <span className="atl-eyebrow">SCORING</span>
          <div style={{ display: "flex", gap: 6 }}>
            {(["speed", "flat"] as ScoringMode[]).map((m) => {
              const on = draft.scoring === m;
              return (
                <div
                  key={m}
                  onClick={() => markDirty({ ...draft, scoring: m })}
                  style={{
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 9,
                    cursor: "pointer",
                    textTransform: "capitalize",
                    background: on ? "#fbf2f3" : "var(--atl-surface)",
                    color: on ? "var(--atl-accent)" : "var(--atl-soft)",
                    border: `1px solid ${on ? "#e2c5ca" : "var(--atl-line)"}`,
                  }}
                >
                  {m}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* Collapsed step strip — hover anywhere on it to reveal full titles */}
          <div className="atl-stepstrip" onMouseEnter={() => setStepsOpen(true)} onMouseLeave={() => setStepsOpen(false)}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "16px 0" }}>
              {draft.questions.map((qq, i) => {
                const on = i === sel;
                const issue = issues[i];
                return (
                  <button
                    key={i}
                    className={"atl-stepnum" + (on ? " atl-stepnum--on" : "")}
                    title={qq.prompt.trim() || "Untitled question"}
                    onClick={() => setSel(i)}
                  >
                    {i + 1}
                    {issue && <span style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: 6, background: "var(--atl-gold)" }} />}
                  </button>
                );
              })}
              <button className="atl-stepnum" title="Add question" onClick={addQuestion} style={{ color: "var(--atl-accent)" }}>
                <Ico name="plus" size={15} />
              </button>
              <button className="atl-stepnum" title="Add from bank" onClick={() => setBankOpen(true)} style={{ color: "var(--atl-gold)" }}>
                <Ico name="bank" size={15} />
              </button>
            </div>

            {stepsOpen && (
              <div className="atl-steppanel">
                <div className="atl-eyebrow" style={{ marginBottom: 14 }}>
                  YOUR BAKE · {draft.questions.length} STEP{draft.questions.length === 1 ? "" : "S"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {draft.questions.map((qq, i) => {
                    const on = i === sel;
                    const issue = issues[i];
                    return (
                      <div
                        key={i}
                        onClick={() => setSel(i)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 11,
                          padding: "10px 11px",
                          borderRadius: 9,
                          cursor: "pointer",
                          background: on ? "#fbf2f3" : "transparent",
                          boxShadow: on ? "inset 2px 0 0 var(--atl-accent)" : "none",
                        }}
                      >
                        <span className="atl-mono" style={{ fontSize: 12, fontWeight: 700, color: on ? "var(--atl-accent)" : "var(--atl-faint)", width: 14, flex: "0 0 auto" }}>
                          {i + 1}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: on ? 600 : 500, color: on ? "var(--atl-accent)" : "var(--atl-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {qq.prompt.trim() || "Untitled question"}
                        </span>
                        {issue && <span title={issue} style={{ width: 7, height: 7, borderRadius: 7, background: "var(--atl-gold)", flex: "0 0 auto" }} />}
                      </div>
                    );
                  })}
                  <div onClick={addQuestion} style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", color: "var(--atl-accent)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    <Ico name="plus" size={14} /> Add question
                  </div>
                  <div onClick={() => setBankOpen(true)} style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", color: "var(--atl-gold)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    <Ico name="bank" size={14} /> Add from bank
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Center — question editor */}
          <div className="bk-scroll" style={{ flex: 1, padding: 28, display: "flex", flexDirection: "column", gap: 22, minWidth: 0, overflowY: "auto" }}>
            {!q ? (
              <div style={{ display: "grid", placeItems: "center", flex: 1, textAlign: "center" }}>
                <div>
                  <h2 className="atl-serif" style={{ fontSize: 26 }}>An empty tray</h2>
                  <p style={{ color: "var(--atl-soft)", fontWeight: 500, marginTop: 8, maxWidth: 320 }}>
                    Add your first question to start building this bake.
                  </p>
                  <button className="atl-btn atl-btn--primary atl-btn--lg" style={{ marginTop: 18 }} onClick={addQuestion}>
                    <Ico name="plus" size={16} /> Add your first question
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="atl-eyebrow atl-eyebrow--accent">RECIPE STEP {selIdx + 1} · PREP THE QUESTION</div>
                  <div style={{ flex: 1 }} />
                  <button className="atl-iconbtn" title="Move up" onClick={() => moveQuestion(selIdx, -1)}>↑</button>
                  <button className="atl-iconbtn" title="Move down" onClick={() => moveQuestion(selIdx, 1)}>↓</button>
                  <button
                    className="atl-iconbtn"
                    title="Save this question to the bank"
                    onClick={async () => {
                      try {
                        await api.createBankQuestion(draftToInput(q));
                        alertDialog({ title: "Saved", message: "This question has been saved to the question bank." });
                      } catch (e) {
                        alertDialog(e instanceof ApiError ? e.message : "Could not save to bank");
                      }
                    }}
                  >
                    <Ico name="bank" size={15} />
                  </button>
                  <button
                    className="atl-iconbtn atl-iconbtn--danger"
                    title="Delete question"
                    onClick={() => deleteQuestion(selIdx)}
                    disabled={draft.questions.length <= 1}
                  >
                    ✕
                  </button>
                </div>

                <QuestionForm question={q} onChange={(nq) => patchQuestion(() => nq)} />
              </>
            )}
          </div>

          {/* Right — live preview (Patisserie player view) · toggleable */}
          {showPreview && (
          <div
            style={{
              width: 322,
              flex: "0 0 322px",
              borderLeft: "1px solid var(--atl-line)",
              background: "var(--atl-surface-2)",
              padding: 22,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "stretch", marginBottom: 16 }}>
              <span className="atl-eyebrow">LIVE PREVIEW</span>
              <span className="atl-chip" style={{ marginLeft: "auto", fontSize: 11.5 }}>Player view</span>
            </div>
            <div style={{ width: 272, borderRadius: 32, overflow: "hidden", border: "7px solid var(--atl-ink)", height: 528, boxShadow: "0 18px 40px rgba(40,25,15,.16)" }}>
              {q ? (
                <div className="pat" style={{ width: "100%", height: "100%" }}>
                  <PreviewPlayer q={q} index={selIdx} total={draft.questions.length} />
                </div>
              ) : (
                <div className="bk bk-grain" style={{ display: "grid", placeItems: "center", height: "100%", padding: 20, textAlign: "center", color: "var(--ink-faint)", fontWeight: 700, fontSize: 13 }}>
                  Add a question to preview it here.
                </div>
              )}
            </div>
            <p className="atl-eyebrow" style={{ marginTop: 16, textAlign: "center", lineHeight: 1.8 }}>
              WHAT THE TEAM SEES · PATISSERIE
            </p>
          </div>
          )}
        </div>
      {bankOpen && <BankPickerModal onClose={() => setBankOpen(false)} onAdd={addFromBank} />}
      {launchOpen && (
        <LaunchModal
          quizId={quizId}
          quizTitle={draft?.title || "Live quiz"}
          onBeforeLaunch={saveBeforeLaunch}
          onClose={() => setLaunchOpen(false)}
        />
      )}
    </div>
  );
}

function PreviewPlayer({ q, index, total }: { q: DraftQuestion; index: number; total: number }) {
  const filled = q.options.filter((o, i) => o.text.trim() || o.imageKey || i < 2);
  return (
    <div className="bk bk-grain" style={{ padding: "14px 13px", display: "flex", flexDirection: "column", gap: 9, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="bk-chip bk-chip--red" style={{ fontSize: 11 }}>
          <span className="mono">Q{index + 1}</span>/{total}
        </span>
        <div style={{ flex: 1 }} />
        <OvenTimer size={40} seconds={String(q.timeLimit)} stroke={4} />
      </div>
      <div className="bk-card" style={{ padding: "12px 14px", textAlign: "center" }}>
        <div className="bk-eyebrow bk-eyebrow--red" style={{ fontSize: 9 }}>
          {questionEyebrow(q.type, q.points)}
        </div>
        <h2 style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.18, marginTop: 6 }}>
          {q.prompt.trim() || "Your question appears here…"}
        </h2>
        {q.imageKey && (
          <img src={mediaUrl(q.imageKey)} alt="" style={{ width: "100%", maxHeight: 90, objectFit: "cover", borderRadius: 8, marginTop: 8 }} />
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        {filled.map((o, i) => {
          const k = OPTION_KEYS[i] ?? "a";
          return (
            <div key={i} className={`bk-opt bk-opt--${k}`} style={{ flex: 1, fontSize: 13, padding: "0 12px" }}>
              <span className="bk-opt__glyph">
                <OptGlyph kind={k} size={16} />
              </span>
              {o.imageKey && (
                <img src={mediaUrl(o.imageKey)} alt="" style={{ width: 26, height: 26, objectFit: "cover", borderRadius: 6 }} />
              )}
              <span style={{ flex: 1 }}>{o.text.trim() || `Option ${k.toUpperCase()}`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
